// 5章 鉄筋工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_BEND = `
<svg viewBox="0 0 660 240" role="img" aria-label="鉄筋の折曲げ形状。180度フックは余長4d以上、135度フックは余長6d以上、90度フックは余長8d以上。折曲げ内法直径DはSD295・SD345のD16以下で3d以上、D19からD38で4d以上、SD390で5d以上">
  <g stroke="currentColor" stroke-width="7" fill="none" stroke-linecap="round">
    <path d="M20 80 H150 A24 24 0 0 1 150 128 H95"/>
    <path d="M240 80 H360 A24 24 0 0 1 377 121 L330 168"/>
    <path d="M460 80 H580 A24 24 0 0 1 604 104 V180"/>
  </g>
  <g fill="currentColor" font-size="14" font-weight="bold" text-anchor="middle">
    <text x="105" y="40">180°フック</text>
    <text x="330" y="40">135°フック</text>
    <text x="550" y="40">90°フック</text>
  </g>
  <g fill="currentColor" font-size="12" text-anchor="middle" opacity="0.85">
    <text x="105" y="160">余長 4d 以上</text>
    <text x="315" y="195">余長 6d 以上</text>
    <text x="560" y="205">余長 8d 以上</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.75">
    <text x="20" y="228">内法直径D：SD295・SD345は D16以下→3d以上、D19〜D38→4d以上　／　SD390→5d以上（d=呼び名の数値）</text>
  </g>
</svg>`;

const SVG_ASSEMBLY = `
<svg viewBox="0 0 640 220" role="img" aria-label="鉄筋組立の模式図。交差部を径0.8ミリ以上の鉄線で結束し、スペーサーで所定のかぶり厚さを保持する">
  <g stroke="currentColor" stroke-width="2" opacity="0.5">
    <line x1="30" y1="180" x2="610" y2="180"/>
  </g>
  <g stroke="currentColor" stroke-width="6" stroke-linecap="round">
    <line x1="60" y1="130" x2="580" y2="130"/>
  </g>
  <g stroke="currentColor" stroke-width="5" stroke-linecap="round" opacity="0.7">
    <line x1="140" y1="100" x2="140" y2="160"/>
    <line x1="320" y1="100" x2="320" y2="160"/>
    <line x1="500" y1="100" x2="500" y2="160"/>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="2">
    <circle cx="140" cy="130" r="12"/>
    <circle cx="500" cy="130" r="12"/>
    <rect x="300" y="150" width="40" height="30" rx="3"/>
  </g>
  <g fill="currentColor" font-size="12.5" opacity="0.9">
    <text x="60" y="70">交差部の要所を鉄線（径0.8mm以上）で結束</text>
    <text x="355" y="170">スペーサー：位置保持＋作業荷重に耐えるもの</text>
    <text x="355" y="188" opacity="0.75">（鋼製は型枠に接する部分に防錆処理）</text>
    <text x="30" y="210" opacity="0.75">下は型枠。スペーサーでかぶり厚さを確保する</text>
  </g>
  <g stroke="currentColor" stroke-width="1.5" opacity="0.7">
    <line x1="150" y1="80" x2="145" y2="118"/>
  </g>
</svg>`;

const SVG_LAP = `
<svg viewBox="0 0 640 240" role="img" aria-label="重ね継手の長さ。フックなしはL1、フックありはL1hで、フック部分は長さに含めない">
  <g stroke="currentColor" stroke-width="7" stroke-linecap="round">
    <line x1="40" y1="60" x2="400" y2="60"/>
    <line x1="240" y1="82" x2="600" y2="82"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="240" y1="100" x2="240" y2="120"/>
    <line x1="400" y1="100" x2="400" y2="120"/>
    <line x1="240" y1="112" x2="400" y2="112"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="320" y="135">L1（フックなしの重ね継手長さ）</text>
  </g>
  <g stroke="currentColor" stroke-width="7" stroke-linecap="round" fill="none">
    <path d="M40 180 H400 A15 15 0 0 1 400 210 H370"/>
    <path d="M600 202 H240 A15 15 0 0 1 240 172 H270"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="240" y1="220" x2="240" y2="238"/>
    <line x1="400" y1="220" x2="400" y2="238"/>
    <line x1="240" y1="230" x2="400" y2="230"/>
  </g>
  <g fill="currentColor" font-size="13" text-anchor="middle">
    <text x="320" y="160" font-weight="bold">L1h（フックあり）＝フック部分は含めない</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.75">
    <text x="420" y="40">・径が違う場合は細い方のdで計算</text>
    <text x="420" y="24">・長さは表5.3.2（Fcと鉄筋種類で決まる）</text>
  </g>
</svg>`;

const SVG_STAGGER = `
<svg viewBox="0 0 640 210" role="img" aria-label="隣り合う継手の位置はずらす。ガス圧接などの継手は隣どうしを同じ位置にせず、位置をずらして配置する">
  <g stroke="currentColor" stroke-width="6" stroke-linecap="round">
    <line x1="40" y1="50" x2="600" y2="50"/>
    <line x1="40" y1="100" x2="600" y2="100"/>
    <line x1="40" y1="150" x2="600" y2="150"/>
  </g>
  <g fill="currentColor">
    <ellipse cx="230" cy="50" rx="16" ry="9"/>
    <ellipse cx="430" cy="100" rx="16" ry="9"/>
    <ellipse cx="230" cy="150" rx="16" ry="9"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="230" y1="65" x2="230" y2="85"/>
    <line x1="430" y1="65" x2="430" y2="85"/>
    <line x1="230" y1="75" x2="430" y2="75"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="330" y="30">継手位置（●）は隣どうしでずらす</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="190">ずらす距離は表5.3.3・特記による（ガス圧接・溶接・機械式は400mm以上が目安）。</text>
    <text x="40" y="206">スラブ筋D16以下と壁筋は対象外。同一箇所に設ける先組み工法等は特記による。</text>
  </g>
</svg>`;

const SVG_ANCHOR = `
<svg viewBox="0 0 640 260" role="img" aria-label="梁主筋の柱内折曲げ定着。全長Lは直線定着の長さ以上、余長は8d以上、仕口面からの投影定着長さLaは柱せいの4分の3倍以上">
  <g fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.6">
    <rect x="360" y="20" width="240" height="220"/>
  </g>
  <g stroke="currentColor" stroke-width="7" stroke-linecap="round" fill="none">
    <path d="M40 80 H520 A18 18 0 0 1 538 98 V200"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5" opacity="0.8">
    <line x1="360" y1="120" x2="360" y2="140"/>
    <line x1="556" y1="120" x2="556" y2="140"/>
    <line x1="360" y1="130" x2="556" y2="130"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold">
    <text x="380" y="152">La（投影定着長さ）</text>
    <text x="380" y="170" font-weight="normal" opacity="0.8">柱せいの3/4倍以上</text>
    <text x="548" y="225">余長 8d 以上</text>
    <text x="60" y="60">梁主筋</text>
    <text x="440" y="40">柱（仕口）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.75">
    <text x="40" y="250">全長L＝表5.3.4の直線定着の長さ以上。フックあり定着の長さを確保できないときの折曲げ定着（図5.3.3）</text>
  </g>
</svg>`;

const SVG_KABURI = `
<svg viewBox="0 0 660 300" role="img" aria-label="かぶり厚さの定義。柱断面で、コンクリート表面から最も外側の帯筋表面までの距離がかぶり厚さ。代表値はスラブ仕上げあり20ミリ、柱や梁の屋内30ミリ、屋外仕上げなし40ミリ、土に接する部分40ミリ、基礎60ミリ">
  <g fill="none" stroke="currentColor">
    <rect x="40" y="40" width="220" height="220" stroke-width="2.5"/>
    <rect x="75" y="75" width="150" height="150" rx="10" stroke-width="4" opacity="0.8"/>
  </g>
  <g fill="currentColor">
    <circle cx="95" cy="95" r="11"/>
    <circle cx="205" cy="95" r="11"/>
    <circle cx="95" cy="205" r="11"/>
    <circle cx="205" cy="205" r="11"/>
  </g>
  <g stroke="currentColor" stroke-width="2">
    <line x1="40" y1="150" x2="71" y2="150"/>
  </g>
  <g fill="currentColor">
    <polygon points="40,150 50,145 50,155"/>
    <polygon points="71,150 61,145 61,155"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold">
    <text x="20" y="285">柱の断面（例）：かぶり厚さは「帯筋の外面」から測る</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="86" y="132">かぶり厚さ</text>
    <text x="120" y="58" opacity="0.7">コンクリート表面</text>
    <text x="120" y="248" opacity="0.7">帯筋 ／ ●主筋</text>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="310" y="52" font-weight="bold">最小かぶり厚さの代表値（表5.3.6）</text>
    <text x="310" y="80">スラブ・非耐力壁（仕上げあり）… 20mm</text>
    <text x="310" y="104">スラブ・非耐力壁（仕上げなし）… 30mm</text>
    <text x="310" y="128">柱・梁・耐力壁（屋内）… 30mm</text>
    <text x="310" y="152">柱・梁・耐力壁（屋外・仕上げなし）… 40mm</text>
    <text x="310" y="176">土に接する柱・梁・スラブ・壁 … 40mm</text>
    <text x="310" y="200">基礎・擁壁・耐圧スラブ … 60mm</text>
    <text x="310" y="232" opacity="0.8">加工用の寸法は「最小かぶり＋10mm」が標準</text>
    <text x="310" y="254" opacity="0.8">柱・梁主筋D29以上→かぶりは径の1.5倍以上</text>
  </g>
</svg>`;

const SVG_AKI = `
<svg viewBox="0 0 640 200" role="img" aria-label="鉄筋相互のあき。隣り合う鉄筋のあきは、粗骨材最大寸法の1.25倍、25ミリ、隣り合う径の平均の1.5倍のうち最大のもの以上とする">
  <g fill="currentColor">
    <circle cx="200" cy="90" r="26"/>
    <circle cx="360" cy="90" r="26"/>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="226" y1="90" x2="334" y2="90"/>
    <line x1="226" y1="80" x2="226" y2="100"/>
    <line x1="334" y1="80" x2="334" y2="100"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="280" y="75">あき</text>
  </g>
  <g fill="currentColor" font-size="13">
    <text x="430" y="60" font-weight="bold">あき ≧ 次のうち最大の値</text>
    <text x="430" y="88">① 粗骨材最大寸法 × 1.25</text>
    <text x="430" y="112">② 25mm</text>
    <text x="430" y="136">③ 隣り合う径の平均 × 1.5</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.75">
    <text x="40" y="170">あきが小さいとコンクリート（粗骨材）が通らず、豆板（ジャンカ）の原因になる。</text>
    <text x="40" y="188">あきは「鉄筋の外面どうしの距離」。D＝鉄筋の最大外径で考える。</text>
  </g>
</svg>`;

const SVG_GAS_QUALITY = `
<svg viewBox="0 0 660 300" role="img" aria-label="ガス圧接部の合格基準。ふくらみの直径は鉄筋径の1.4倍以上、ふくらみの長さは1.1倍以上、圧接面のずれは4分の1以下、偏心量は5分の1以下、折れ曲がりは2度以下、片ふくらみは5分の1以下">
  <g stroke="currentColor" stroke-width="0" fill="currentColor" opacity="0.9">
    <rect x="40" y="128" width="200" height="24" rx="3"/>
    <rect x="420" y="128" width="200" height="24" rx="3"/>
    <ellipse cx="330" cy="140" rx="95" ry="45"/>
  </g>
  <g stroke="currentColor" stroke-width="1.6">
    <line x1="235" y1="205" x2="425" y2="205"/>
    <line x1="235" y1="195" x2="235" y2="215"/>
    <line x1="425" y1="195" x2="425" y2="215"/>
    <line x1="660" y1="0" x2="660" y2="0"/>
    <line x1="255" y1="95" x2="255" y2="185"/>
    <line x1="245" y1="95" x2="265" y2="95"/>
    <line x1="245" y1="185" x2="265" y2="185"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="330" y="230">ふくらみの長さ ≧ 1.1d</text>
    <text x="150" y="90">ふくらみの直径 ≧ 1.4d</text>
  </g>
  <g fill="currentColor" font-size="12.5" text-anchor="middle" opacity="0.9">
    <text x="330" y="50">圧接面はふくらみの中央付近にあること</text>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="40" y="262">・圧接面のずれ ≦ d/4　・中心軸の偏心 ≦ d/5　・折れ曲がり ≦ 2°　・片ふくらみ ≦ d/5</text>
    <text x="40" y="284">・焼割れ、へこみ、垂下がり、内部欠陥がないこと（d＝細い方の鉄筋径）</text>
  </g>
</svg>`;

const SVG_GAS_TEST = `
<svg viewBox="0 0 640 250" role="img" aria-label="圧接完了後の試験の流れ。全ての圧接部に外観試験を行い、ロットごとに30か所を抜き取って超音波探傷試験を行う。1ロットは1組の作業班が1日に行った圧接箇所。全箇所合格でロット合格">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30"  y="40" width="160" height="64" rx="8"/>
    <rect x="240" y="40" width="160" height="64" rx="8"/>
    <rect x="450" y="16" width="170" height="52" rx="8"/>
    <rect x="450" y="96" width="170" height="52" rx="8"/>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="190" y1="72" x2="230" y2="72"/>
    <line x1="400" y1="60" x2="440" y2="42"/>
    <line x1="400" y1="84" x2="440" y2="120"/>
  </g>
  <g fill="currentColor">
    <polygon points="240,72 228,66 228,78"/>
    <polygon points="450,40 437,38 441,48"/>
    <polygon points="450,122 437,120 441,110"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="110" y="66">外観試験</text>
    <text x="320" y="66">超音波探傷試験</text>
    <text x="535" y="38">全箇所合格</text>
    <text x="535" y="118">1か所でも不合格</text>
  </g>
  <g fill="currentColor" font-size="11.5" text-anchor="middle" opacity="0.85">
    <text x="110" y="86">全数・目視（ノギス等併用）</text>
    <text x="320" y="86">1ロットから無作為に30か所</text>
    <text x="535" y="56">→ ロット合格</text>
    <text x="535" y="136">→ 作業中止・残り全数試験</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="30" y="190">・1ロット＝1組の作業班が1日に行った圧接箇所</text>
    <text x="30" y="212">・超音波探傷試験か引張試験かは特記による（特記がなければ超音波探傷試験）</text>
    <text x="30" y="234">・不合格ロットは原因調査＋再発防止策を定めて監督職員の承諾を受けてから再開（5.4.11）</text>
  </g>
</svg>`;

const SVG_GAS_NG = `
<svg viewBox="0 0 640 240" role="img" aria-label="外観試験で不合格となった圧接部の措置。ふくらみの直径や長さの不足と折れ曲がりは再加熱して修正できる。ずれ、偏心、片ふくらみの超過や焼割れなどの欠陥は圧接部を切り取って再圧接する">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30"  y="40" width="280" height="150" rx="10"/>
    <rect x="330" y="40" width="280" height="150" rx="10"/>
  </g>
  <g fill="currentColor" font-size="14" font-weight="bold" text-anchor="middle">
    <text x="170" y="66">🔥 再加熱で修正できる</text>
    <text x="470" y="66">✂ 切り取って再圧接</text>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="50" y="96">・ふくらみの直径不足</text>
    <text x="50" y="120">・ふくらみの長さ不足</text>
    <text x="50" y="144">・折れ曲がり（2°超）</text>
    <text x="350" y="96">・圧接面のずれ超過（d/4超）</text>
    <text x="350" y="120">・偏心量の超過（d/5超）</text>
    <text x="350" y="144">・片ふくらみの超過（d/5超）</text>
    <text x="350" y="168">・焼割れ・へこみ・垂下がり等の欠陥</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="30" y="220">措置後は改めて外観試験＋超音波探傷試験を行い、記録を整理して監督職員に提出する。</text>
  </g>
</svg>`;

const SVG_MECHANICAL = `
<svg viewBox="0 0 640 220" role="img" aria-label="機械式継手の模式図。カップラーまたはスリーブに両側から鉄筋を挿入し、鉄筋端部の挿入マークで挿入長さを確認する。モルタル充填式は空気排出孔からの排出で充填を確認する">
  <g fill="currentColor" opacity="0.9">
    <rect x="40" y="98" width="180" height="20" rx="3"/>
    <rect x="420" y="98" width="180" height="20" rx="3"/>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="3">
    <rect x="210" y="84" width="220" height="48" rx="8"/>
  </g>
  <g stroke="currentColor" stroke-width="2">
    <line x1="185" y1="70" x2="185" y2="95"/>
    <line x1="455" y1="70" x2="455" y2="95"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold" text-anchor="middle">
    <text x="320" y="70">カップラー／スリーブ</text>
    <text x="185" y="58">挿入マーク</text>
    <text x="455" y="58">挿入マーク</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="40" y="170">外観試験（全数）で見るポイント：</text>
    <text x="40" y="192">①部品に損傷がない ②挿入マークがある ③マークが所定位置（＝挿入長さOK） ④充填材が溢れ出ている</text>
    <text x="40" y="214" opacity="0.8">※ 端面が直角な鉄筋を使う。挿入マークの位置・大きさは工法ごとの定めによる</text>
  </g>
</svg>`;

const ch05: Record<string, Commentary> = {
  "5.1.1": {
    plainSummary:
      "この章はRC造・SRC造などの鉄筋工事のルール。1章（各章共通事項）とセットで適用する。",
    why: "鉄筋はコンクリートの中に隠れてしまう「見えなくなる工事」の代表です。だからこそ加工・組立・継手・検査のルールが細かく決められています。",
  },

  "5.1.2": {
    plainSummary:
      "鉄筋工事のゴールは3つ。①所定の材料を使う ②所定の形状・寸法で所定の位置に保持される ③継手・定着が力を伝えられる。",
    why: "この3つは5章全体の「目次」のようなものです。材料（2節）、加工・組立・かぶり（3節）、継手（4〜6節）と、この基本要求品質を実現するための各論が続きます。",
    glossary: [
      {
        term: "基本要求品質",
        meaning:
          "工事目的物の引渡し時に持っているべき品質。各章の冒頭で定義される（1.1.2参照）。",
      },
    ],
  },

  "5.1.3": {
    plainSummary:
      "コンクリートを打つ前に、主要な配筋について監督職員の検査（配筋検査）を受ける。見るのは種類・径・数量・かぶり厚さ・間隔・あき・位置など。",
    why: "コンクリートを打った瞬間、鉄筋は二度と見えなくなります。配筋検査は「隠れる前の最後のチェック」であり、鉄筋工事で最も重要なイベントです。検査前に自主検査で指摘をつぶしておくのがプロの仕事です。",
    points: [
      "検査項目を配筋検査チェックシートにして自主検査してから検査に臨む",
      "配筋写真（黒板に部位・径・ピッチを明記）は隠ぺい前の必須記録（1.2.4）",
      "指摘事項は是正して再確認を受けてから打込みへ",
    ],
  },

  "5.2.1": {
    plainSummary:
      "鉄筋はJIS G 3112の鉄筋コンクリート用棒鋼（SR235、SR295、SD295、SD345、SD390）。どれを使うかは特記による。",
    why: "SRは丸鋼、SDは異形鉄筋（表面に節がある）。数字は降伏点の強さ（N/mm²）です。現在の主流はSD295やSD345の異形鉄筋で、節がコンクリートとの付着を高めます。",
    glossary: [
      {
        term: "異形鉄筋（SD）",
        meaning:
          "表面にリブや節のある鉄筋。コンクリートとの付着力が高い。SD345なら降伏点345N/mm²以上。",
      },
      {
        term: "丸鋼（SR）",
        meaning: "表面がつるつるの丸い鉄筋。現在はあまり使われない。",
      },
    ],
  },

  "5.2.2": {
    plainSummary:
      "溶接金網はJIS G 3551による。鉄線の形状・網目寸法・径は特記による。",
    glossary: [
      {
        term: "溶接金網（ワイヤーメッシュ）",
        meaning:
          "細い鉄線を格子状に溶接した網。土間コンクリートやスラブの補強に使う。",
      },
    ],
  },

  "5.2.3": {
    plainSummary:
      "鉄筋の品質を試験で証明する場合は、JISまたは建築基準法に基づく方法で行う。",
    why: "通常はJISマークとミルシート（鋼材検査証明書）で品質を確認します。試験が必要になるのは規格が確認できない場合などです。",
    glossary: [
      {
        term: "ミルシート",
        meaning:
          "鉄鋼メーカーが発行する鋼材の検査証明書。化学成分や強度試験の結果が記載されている。",
      },
    ],
  },

  "5.3.1": {
    plainSummary:
      "鉄筋は常温で加工する（熱して曲げない）。曲がり・損傷のある鉄筋は使わない。点付け溶接やアークストライクは厳禁。",
    why: "鉄筋を加熱すると材質が変化して強度が落ちます。点付け溶接やアークストライク（溶接棒が触れて火花が飛ぶこと）も局部的な加熱で鉄筋を傷めるため禁止です。「鉄筋に熱を加えない」が大原則です。",
    points: [
      "現場で鉄筋をバーナーで炙って曲げるのは絶対NG",
      "組立時に位置がずれても、溶接で仮止めしない（結束線を使う）",
      "コイル状の鉄筋は損傷を与えずに直線に戻してから使用",
    ],
    glossary: [
      {
        term: "アークストライク",
        meaning:
          "溶接棒やホルダが母材に触れて瞬間的にアークが飛ぶこと。小さな傷でも鉄筋の破断起点になり得る。",
      },
    ],
  },

  "5.3.2": {
    plainSummary:
      "切断はシヤーカッター等で行う（ガス切断ではなく）。柱の四隅の主筋の重ね継手、帯筋・あばら筋などの末端部にはフックを付ける。折曲げの内法直径と余長は表5.3.1による。",
    why: "フックは鉄筋の抜け出しを防ぐ「いかり」です。特に地震時に力が集中する柱の四隅や、コンクリートを拘束する帯筋・あばら筋には必須。折曲げの内法直径が小さすぎると鉄筋の外側に割れが入るため、径（d）に応じた最小値が決まっています。",
    points: [
      "フックが必要な6箇所（柱四隅の重ね継手・柱頭定着、梁出隅・下端両隅、煙突、杭基礎ベース筋、帯筋・あばら筋・幅止め筋）を覚える",
      "内法直径：SD295・SD345のD16以下→3d、D19〜D38→4d、SD390→5d",
      "余長：180°→4d以上、135°→6d以上、90°→8d以上",
    ],
    glossary: [
      {
        term: "フック",
        meaning: "鉄筋の端部を折り曲げた部分。定着力を高め、抜け出しを防ぐ。",
      },
      {
        term: "余長",
        meaning: "フックの折曲げ後の直線部分の長さ。dの倍数で規定される。",
      },
      {
        term: "帯筋（フープ）",
        meaning:
          "柱の主筋を囲む横方向の鉄筋。せん断補強とともに主筋の座屈を防ぐ。",
      },
      {
        term: "あばら筋（スタラップ）",
        meaning: "梁の主筋を囲む鉄筋。梁のせん断破壊を防ぐ。",
      },
    ],
    diagrams: [
      {
        svg: SVG_BEND,
        caption: "鉄筋の折曲げ形状と余長（表5.3.1）",
      },
    ],
  },

  "5.3.3": {
    plainSummary:
      "継手部と交差部の要所を径0.8mm以上の鉄線で結束し、スペーサー・吊金物で堅固に組み立てる。スペーサーは位置保持＋作業荷重に耐えるもの。",
    why: "結束線は鉄筋を「仮に留めている」だけで、力を伝えるのはコンクリートとの付着です。それでも打込み時の衝撃で動かないよう、要所の結束とスペーサーによる保持が重要です。スペーサーが弱いと人が乗っただけでかぶり厚さが失われます。",
    points: [
      "スラブのスペーサーは作業員が乗っても潰れないものを選ぶ",
      "鋼製スペーサーは型枠に接する部分の防錆処理を確認（錆汁の原因になる）",
      "結束線の切れ端を型枠内に残さない（錆の原因）",
    ],
    glossary: [
      {
        term: "スペーサー",
        meaning:
          "鉄筋と型枠の間に入れて、かぶり厚さを確保する部品。モルタル製・鋼製・プラスチック製がある。",
      },
    ],
    diagrams: [
      {
        svg: SVG_ASSEMBLY,
        caption: "結束とスペーサーによる組立（5.3.3）",
      },
    ],
  },

  "5.3.4": {
    plainSummary:
      "継手は重ね・ガス圧接・機械式・溶接の4種類で、どれを使うかは特記による。D35以上は重ね継手NG。重ね継手の長さは表5.3.2、定着の長さは表5.3.4。隣り合う継手はずらす。",
    why: "鉄筋は運搬できる長さに限りがあるため、必ずどこかで継ぎます。継手は構造の弱点になりやすいので、①太物（D35以上）は重ね継手にしない ②長さはコンクリート強度と鉄筋種類で決める ③隣どうしで位置をずらして弱点を一断面に集中させない、という3つの原則があります。径が異なる重ね継手は細い方の径で長さを計算します。",
    points: [
      "重ね継手長さの例：SD345・Fc24なら L1=40d（フックあり L1h=30d）",
      "L1hはフック部分を含まない長さ。軽量コンクリートは＋5d",
      "隣り合う継手はずらす（スラブ筋D16以下・壁筋は除く）",
      "梁主筋の柱内折曲げ定着は投影長さLa≧柱せいの3/4、余長8d以上",
    ],
    glossary: [
      {
        term: "定着",
        meaning:
          "鉄筋の端部を他の部材（柱・梁など）に必要な長さだけ埋め込み、力を伝えられるようにすること。",
      },
      {
        term: "重ね継手",
        meaning:
          "2本の鉄筋を所定の長さだけ重ねて結束する継手。コンクリートの付着で力を伝える。",
      },
      {
        term: "L1・L1h",
        meaning:
          "重ね継手の長さ（フックなし・フックあり）。コンクリート強度Fcと鉄筋種類で決まる（表5.3.2）。",
      },
    ],
    diagrams: [
      {
        svg: SVG_LAP,
        caption: "重ね継手の長さ L1・L1h（図5.3.1）",
      },
      {
        svg: SVG_STAGGER,
        caption: "隣り合う継手の位置はずらす（表5.3.3）",
      },
      {
        svg: SVG_ANCHOR,
        caption: "梁主筋の柱内折曲げ定着（図5.3.3）",
      },
    ],
  },

  "5.3.5": {
    plainSummary:
      "最小かぶり厚さは部位ごとに決まっている（スラブ20〜基礎60mm）。加工用の寸法は最小かぶり＋10mmが標準。鉄筋相互のあきは「粗骨材×1.25、25mm、径平均×1.5」の最大値以上。",
    why: "かぶり厚さはコンクリートが鉄筋を錆と火災から守る「防護服」の厚さです。薄いと中性化で鉄筋が錆びて構造寿命が縮みます。施工誤差があっても最小値を割らないよう、加工段階では＋10mmして作ります。あきが狭いと粗骨材が通れず、ジャンカ（豆板）の原因になります。",
    points: [
      "かぶり厚さは「最も外側の鉄筋（帯筋・あばら筋）の表面」から測る",
      "土に接する部分40mm・基礎60mmは捨コンクリートの厚さを含まない",
      "柱・梁主筋にD29以上を使う場合はかぶり＝径の1.5倍以上",
      "配筋検査では要所のかぶりをスケールで実測して写真に残す",
    ],
    glossary: [
      {
        term: "かぶり厚さ",
        meaning:
          "コンクリート表面から最も外側の鉄筋表面までの距離。耐久性・耐火性・付着力を左右する。",
      },
      {
        term: "あき",
        meaning:
          "隣り合う鉄筋の外面どうしの距離。コンクリートが通るために必要。",
      },
      {
        term: "中性化",
        meaning:
          "空気中の二酸化炭素でコンクリートのアルカリ性が失われる現象。鉄筋が錆びやすくなる。",
      },
    ],
    diagrams: [
      {
        svg: SVG_KABURI,
        caption: "かぶり厚さの定義と最小かぶり厚さ（表5.3.6）",
      },
      {
        svg: SVG_AKI,
        caption: "鉄筋相互のあきの最小値（図5.3.6）",
      },
    ],
  },

  "5.3.6": {
    plainSummary:
      "組立後の鉄筋の上を直接歩かない（歩み板を敷く）。打込み時も鉄筋の乱れを最小限にし、かぶり・位置・間隔を保つ。",
    why: "せっかく検査に合格した配筋も、人が乗って曲げたり、打込みで動いたりすれば台無しです。「配筋検査合格＝ゴール」ではなく、打込み完了まで配筋を守り続けるのが仕事です。",
    points: [
      "スラブ配筋後は歩み板（足場板）を敷いてから作業する",
      "打込み中に鉄筋がずれたら、その場で直せる体制（鉄筋工の立会い）をとる",
    ],
  },

  "5.3.7": {
    plainSummary:
      "各部の配筋の詳細（柱・梁・壁・スラブの標準配筋要領）は特記による。",
    why: "実際の配筋詳細は構造図と、多くの現場で「配筋標準図」または公共建築工事標準図等により指定されます。必ず特記と構造図を確認しましょう。",
  },

  "5.4.1": {
    plainSummary:
      "この節は、酸素・アセチレン炎で鉄筋を熱し、圧力を加えて接合する「ガス圧接」のルール。",
    why: "ガス圧接は鉄筋どうしを溶かさずに、赤熱状態で押し付けて金属組織レベルで一体化させる継手です。太い鉄筋（D19以上が目安）の継手として最も普及しています。",
    glossary: [
      {
        term: "ガス圧接",
        meaning:
          "鉄筋の端面どうしを突き合わせ、加熱しながら加圧して接合する工法。溶接と違い母材を溶かさない。",
      },
    ],
  },

  "5.4.2": {
    plainSummary:
      "圧接作業はJIS Z 3881の技術検定に基づく技能資格者が行う。",
    why: "圧接の品質は職人の腕に大きく左右されます。圧接技量資格は1種〜4種まであり、扱える鉄筋の径が異なります（例：1種はD25以下、2種はD32以下など）。作業前に資格証明の提出が必要です（1.5.3）。",
    points: [
      "作業班ごとに資格証（圧接技量資格者証）のコピーを提出",
      "資格の種別と施工する鉄筋径が合っているか確認",
    ],
  },

  "5.4.3": {
    plainSummary:
      "圧接部の外観試験・超音波探傷試験を行う技能資格者は、圧接の知識・経験を持ち、かつその工事の圧接品質管理をしていない者（＝第三者性のある検査員）。",
    why: "「自分の仕事を自分で採点しない」という品質管理の大原則です。施工した班とは別の、利害のない資格者が検査することで、検査の信頼性を担保します。",
  },

  "5.4.4": {
    plainSummary:
      "圧接部の合格基準：ふくらみ直径≧1.4d・長さ≧1.1d、圧接面のずれ≦d/4、偏心≦d/5、折れ曲がり≦2°、片ふくらみ≦d/5、有害な欠陥なし（dは細い方の径）。",
    why: "適切に圧接された部分は、母材と同等以上の強度を持ちます。ふくらみの形は圧接の良否を映す鏡で、小さすぎる＝加圧・加熱不足、ずれや偏心＝セットの不良を意味します。数値は全て「細い方の鉄筋径d」を基準に判定します。",
    points: [
      "1.4d・1.1d・d/4・d/5・2°の5つの数字はそのまま覚える",
      "外観検査ではノギスでふくらみ径を実測",
      "径違い圧接の判定は細い方のdで行う",
    ],
    diagrams: [
      {
        svg: SVG_GAS_QUALITY,
        caption: "圧接部の合格基準（5.4.4）",
      },
    ],
  },

  "5.4.5": {
    plainSummary:
      "圧接器具は整備されたものを使う。鉄筋の種類が異なる場合と径差7mm超は圧接禁止。ただしSD345×SD390の組合せだけは可。",
    why: "種類や径が違いすぎる鉄筋は、加熱・加圧のバランスが取れず健全な圧接になりません。「径差7mm以内」「異種はSD345×SD390のみ可」はガス圧接の基本ルールです。",
    points: [
      "D22×D29（径差7mm）はOK、D22×D32（径差10mm）はNG",
      "SD295×SD345の圧接は不可（異種でOKなのはSD345×SD390のみ）",
    ],
  },

  "5.4.6": {
    plainSummary:
      "圧接すると鉄筋は1箇所につき鉄筋径程度縮む。その縮み代を見込んで切断・加工する。端面は直角で平滑に。",
    why: "加圧で押し込む分だけ鉄筋は短くなります（縮み代＝約1d）。これを忘れて加工すると、組み上がったときに寸法が足りなくなります。",
    points: [
      "圧接箇所数×約1dの縮み代を加工長さに織り込む",
    ],
    glossary: [
      {
        term: "縮み代（アプセット）",
        meaning:
          "圧接時の加圧で鉄筋が短くなる分。1箇所あたり鉄筋径程度を見込む。",
      },
    ],
  },

  "5.4.7": {
    plainSummary:
      "圧接前の端面には錆・油脂・塗料・セメントペースト等が付いていないこと。端面は直角・平滑に仕上げ、その処理は圧接作業当日に行う。",
    why: "端面の汚れや酸化膜は圧接不良（内部欠陥）の最大の原因です。前日に処理すると一晩で酸化被膜ができるため、「当日処理」が原則になっています。",
    points: [
      "端面処理（グラインダー掛け）は圧接当日に実施",
      "冷間直角切断機で切った直角端面を使うのが基本",
    ],
  },

  "5.4.8": {
    plainSummary:
      "降雨・降雪・強風時は圧接作業をしない。ただし風除け・覆いを設置すれば作業できる。ガス容器は法令に基づき適切な温度管理・直射日光からの保護を行う。",
    why: "雨や風は加熱温度を不安定にし、急冷による割れの原因になります。「天気が悪い＝即中止」ではなく、養生設備を整えれば施工できる、という規定のかたちも覚えておきましょう。",
  },

  "5.4.9": {
    plainSummary:
      "圧接器で隙間なく突き合わせ、還元炎で密着させた後、中性炎で径の2倍程度の範囲を加熱しながら加圧する。火色が消えるまで圧接器を外さない。密着前に火炎異常があれば切り取って再圧接。",
    why: "最初は還元炎（酸素少なめ）で端面の酸化を防ぎ、密着後は中性炎で本加熱する、という2段階が品質のキモです。密着前の火炎異常は端面が酸化してしまうため、やり直し（切取り）しかありません。",
    glossary: [
      {
        term: "還元炎・中性炎",
        meaning:
          "ガス炎の種類。還元炎はアセチレン過多で酸化を防ぐ炎、中性炎は酸素とアセチレンが釣り合った炎。",
      },
    ],
  },

  "5.4.10": {
    plainSummary:
      "圧接後の試験は2段構え。①外観試験＝全数を目視（＋ノギス等） ②超音波探傷試験＝1ロット（1班1日分）から30箇所抜取り。全箇所合格でロット合格。",
    why: "外観で分かる不良は全数チェック、内部欠陥は抜取りの超音波探傷でカバーする仕組みです。「1班1日＝1ロット」「30箇所」「全数合格でロット合格」という数字は現場管理の基本知識です。",
    points: [
      "ロットの考え方：作業班ごと・日ごとに区切る",
      "抜取りは無作為に30箇所（偏らせない）",
      "特記がなければ超音波探傷試験（引張試験は特記時のみ）",
    ],
    glossary: [
      {
        term: "超音波探傷試験（UT）",
        meaning:
          "超音波を当てて反射波から内部欠陥を探す非破壊試験。圧接部を壊さずに検査できる。",
      },
      {
        term: "ロット",
        meaning: "品質管理の単位。ここでは1組の作業班が1日に行った圧接箇所。",
      },
    ],
    diagrams: [
      {
        svg: SVG_GAS_TEST,
        caption: "圧接完了後の試験の流れ（5.4.10）",
      },
    ],
  },

  "5.4.11": {
    plainSummary:
      "外観不合格の措置は2択：ふくらみ不足・折れ曲がりは「再加熱で修正」、ずれ・偏心・片ふくらみの超過や焼割れ等は「切り取って再圧接」。UT不合格ロットは作業中止→残り全数試験→再発防止策の承諾。",
    why: "「直せる不良」と「作り直す不良」の区別が明確に決まっています。形が足りないだけなら熱と圧力で直せますが、位置がずれたものは直しようがないので切除です。ロット不合格は個別の問題ではなく作業班の問題とみなし、全数検査＋原因究明が求められます。",
    points: [
      "再加熱修正：ふくらみ直径・長さ不足、折れ曲がり",
      "切取り再圧接：ずれd/4超、偏心d/5超、片ふくらみd/5超、焼割れ・へこみ・垂下がり",
      "措置後は外観＋UTを再実施し、記録を監督職員に提出",
    ],
    diagrams: [
      {
        svg: SVG_GAS_NG,
        caption: "外観試験不合格時の措置の分岐（5.4.11）",
      },
    ],
  },

  "5.5.1": {
    plainSummary: "この節は機械式継手（カップラーやスリーブで鉄筋をつなぐ工法）に適用する。",
    glossary: [
      {
        term: "機械式継手",
        meaning:
          "ねじ節鉄筋継手・モルタル充填継手・端部ねじ加工継手など、金物で鉄筋を機械的に接合する継手。火を使わず天候の影響を受けにくい。",
      },
    ],
  },

  "5.5.2": {
    plainSummary:
      "機械式継手の作業は技能資格者が行う（1.5.3による）。各工法の講習を受けた資格者が施工する。",
  },

  "5.5.3": {
    plainSummary:
      "機械式継手は告示第1463号の性能を持つ工法とする。端面が直角な鉄筋を使い、鉄筋端部に「挿入マーク」を付けて挿入長さを管理する。隣り合う継手位置はガス圧接と同じくずらす。",
    why: "機械式継手の品質は「鉄筋がスリーブに所定の長さだけ入っているか」でほぼ決まります。中に入った鉄筋は見えないため、あらかじめ鉄筋に付けた挿入マークの位置で挿入長さを外から確認できるようにします。これが検査（5.5.5）の決め手になります。",
    points: [
      "挿入マークの位置・大きさは工法ごとのマニュアルによる",
      "端面が直角でない鉄筋は使わない",
      "適用箇所・種類・鉄筋相互のあきは特記を確認",
    ],
    diagrams: [
      {
        svg: SVG_MECHANICAL,
        caption: "機械式継手と挿入マークによる管理（5.5.3〜5.5.5）",
      },
    ],
  },

  "5.5.4": {
    plainSummary:
      "継手部の試験は、機械式継手の知識・経験を持ち、その工事の継手品質管理をしていない技能資格者（第三者性のある検査員）が行う。",
    why: "ガス圧接（5.4.3）と同じ考え方で、施工者と検査者を分離して検査の信頼性を確保します。",
  },

  "5.5.5": {
    plainSummary:
      "施工後は全数の外観試験。見るのは①部品の損傷 ②挿入マークの有無 ③マーク位置（挿入長さ） ④グラウト／モルタルの溢出（充填確認）など、工法の種類ごとの項目。超音波測定試験の適用は特記による。",
    why: "ねじ節鉄筋継手なら「カップラー両端からグラウトが溢れているか」、モルタル充填継手なら「空気排出孔からモルタルが出ているか」が充填完了のサインです。溢れていない＝中に空洞があるかもしれない、と考えます。",
    points: [
      "合わせマークが「ずれていること」が合格（ナットが締め付けられた証拠）という項目に注意",
      "工法ごとに合否基準が違う（表5.5.1〜5.5.3）",
    ],
  },

  "5.5.6": {
    plainSummary:
      "外観不合格の措置は不良の内容ごとに決まっている：部品損傷→交換または切取り再施工、マークなし・位置不良→超音波測定試験で挿入長さを確認、充填不良→原則切取り再施工、締付け不足→トルク再導入。",
    why: "機械式継手は「確認できないなら測って確かめる、直せないなら作り直す」という発想です。挿入マークが無い場合でも即不合格ではなく、超音波で実際の挿入長さを測定して判定できます。",
  },

  "5.6.1": {
    plainSummary: "この節は溶接継手（突合せ溶接・重ねアーク溶接）に適用する。",
  },

  "5.6.2": {
    plainSummary:
      "溶接継手の作業は技能資格者が行う。突合せ溶接はJIS Z 3882の検定、重ねアーク溶接は7章（鉄骨工事）の溶接資格に準じた試験による。",
  },

  "5.6.3": {
    plainSummary:
      "溶接継手は告示第1463号の性能を持つこと。端面が直角な鉄筋を使う（重ねアーク溶接を除く）。D16以下の溶接は重ねアーク溶接とする。隣り合う継手はずらす。",
    why: "細物（D16以下）は突合せ溶接が難しいため、重ねてアーク溶接する方式に限定されています。溶接継手は5.3.1の「点付け溶接禁止」とは別物で、告示性能を満たす正式な継手工法です。",
  },

  "5.6.4": {
    plainSummary:
      "溶接部の試験は、溶接継手の知識・経験を持ち、その工事の溶接品質管理をしていない技能資格者（第三者性のある検査員）が行う。",
  },

  "5.6.5": {
    plainSummary:
      "溶接後は全数の外観試験（割れ・溶込み不良・ピット・アンダーカット等、基準は表5.6.1）＋1ロット30箇所の超音波探傷試験。ロットの考え方はガス圧接と同じ（1班1日）。",
    points: [
      "代表的な基準：割れ・溶込み不良・溶落ち→あってはならない、偏心≦0.1d、折れ曲がり≦3°",
      "1ロット＝1組の作業班が1日に行った継手、抜取り30箇所、全数合格でロット合格",
    ],
    glossary: [
      {
        term: "アンダーカット",
        meaning: "溶接ビードの端で母材が掘れて凹んだ欠陥。0.5mm以下が合格。",
      },
      {
        term: "ピット",
        meaning: "ビード表面にできた小さな穴。直径0.5mm以下が合格。",
      },
    ],
  },

  "5.6.6": {
    plainSummary:
      "外観不合格→補修または再溶接して再試験。UT不合格ロット→作業中止・原因調査・残り全数UT・再発防止策の承諾を受けて再開。措置後は記録を監督職員に提出。",
    why: "流れはガス圧接の不合格措置（5.4.11）と同じ構造です。「不合格＝その1箇所の問題」ではなく「その班のその日の施工全体を疑う」のがロット管理の考え方です。",
  },
};

export default ch05;
